import { Loader } from '@googlemaps/js-api-loader';
import { getMapsKeys } from './mapsKey';

// Singleton instance of the Google Maps Loader
let loaderInstance: Loader | null = null;
let loadingPromise: Promise<typeof google> | null = null;
let isLoaded = false;

/**
 * Shared Google Maps API Loader
 * Ensures the API is only loaded once with all necessary libraries
 * Prevents "Loader must not be called again with different options" error
 */
export const getGoogleMapsLoader = async (): Promise<typeof google> => {
  // If already loaded, return immediately
  if (isLoaded && window.google) {
    return window.google;
  }

  // If currently loading, wait for the existing promise
  if (loadingPromise) {
    return loadingPromise;
  }

  // Create new loading promise
  loadingPromise = (async () => {
    try {
      console.debug('[GoogleMapsLoader] Initializing Google Maps API...');
      
      const { mapsApiKey, source } = await getMapsKeys();
      console.debug(`[GoogleMapsLoader] Using API key from: ${source}`);

      if (!loaderInstance) {
        loaderInstance = new Loader({
          apiKey: mapsApiKey,
          version: 'weekly',
          libraries: ['places', 'geometry']
        });
      }

      const google = await loaderInstance.load();
      isLoaded = true;
      
      return google;
    } catch (error) {
      console.error('[GoogleMapsLoader] Failed to load Google Maps API:', error);
      loadingPromise = null; // Reset so it can be retried
      throw error;
    }
  })();

  return loadingPromise;
};

/**
 * Check if Google Maps is already loaded
 */
export const isGoogleMapsLoaded = (): boolean => {
  return isLoaded && typeof window !== 'undefined' && typeof window.google !== 'undefined';
};

/**
 * Reset the loader (useful for testing)
 */
export const resetGoogleMapsLoader = () => {
  loaderInstance = null;
  loadingPromise = null;
  isLoaded = false;
};
